import React, { useState, useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useTimer } from 'react-timer-hook';
import { TextInput, CheckboxInput } from 'components/CreateAccountForm/Inputs';
import { requestEmailConfirmation, checkEmailVerification, requestPhoneVerification, checkPhoneVerification, lookupPhoneLine } from 'api/account';
import { useBearerTokenContext, useTenantContext, useReturnURLContext } from 'utilities/contexts';
import { VALIDATION_SCHEMA } from 'components/App/CreateAccount/AppLayout';


export function EmailVerify({
    errors, register, getValues, resetField, setError, fieldUpdate, setValue,
    emailAddress, emailIsDirty, isCommunity, emailVerified, trigger, clearErrorMessage, handleServerErrors
}) {

    const bearer = useBearerTokenContext();
    const tenant = useTenantContext();
    const returnURL = useReturnURLContext();

    const [confirmationRquested, setConfirmationRquested] = useState(0);

    // verify functions
    const handleEmailVerification = (e) => {

        if (emailIsDirty) {
            setError('emailAddress', { type: 'server', message: 'Unsaved email changes' });
            return;
        }

        const [emailAddressGuid] = getValues(['emailAddressGuid']);
        const emailRequestVerify = { "EmailAddressGuid": emailAddressGuid, "ReturnUrl": returnURL };

        requestEmailConfirmation(emailRequestVerify, bearer, tenant).then(
            (value) => {
                setConfirmationRquested(confirmationRquested + 1);
            },
            (reason) => {
                handleServerErrors(reason, false);
            });
    }

    const handleEmailCheck = (e) => {

        if (emailIsDirty) {
            setError('emailAddress', { type: 'server', message: 'Unsaved email changes' });
            return;
        }

        trigger(['emailAddress', 'emailCode']).then(triggerResult => {
            if (triggerResult === false) return;

            const [emailAddressGuid, emailCode] = getValues(['emailAddressGuid', 'emailCode']);

            const emailAddressCheckVerify = {
                'emailAddressGuid': emailAddressGuid,
                'emailToken': emailCode
            };

            checkEmailVerification(emailAddressCheckVerify, bearer, tenant).then(
                (value) => {
                    clearErrorMessage();
                    if (value === true) {
                        setValue('emailVerified', true);
                    } else {
                        setError('emailCode', { type: 'server', message: 'Invalid code' });
                    }
                },
                (reason) => {
                    handleServerErrors(reason, false);
                });


        });
    }

    // update buttons to put over the email field
    const buttonOverlay = emailIsDirty ? (
        <>
            <Button variant="" className="error" title="Cancel email address change"
                onClick={() => resetField('emailAddress')}>X</Button>
            <Button variant="" className="success" title="Confirm email address change"
                onClick={() => fieldUpdate(emailAddress, 'newAddress', 'emailAddress', 'email address')}>&#10003;</Button>
        </>
    ) : <></>;

    return (
        <>
            {!emailVerified && (confirmationRquested === 0) && 
                <>
                    <div className="align-with-inputs">
                        <div className='verify-step-label'>{isCommunity ? <>Step 1:&nbsp;</> : <></>}Please verify your email address below.</div>

                        <hr className="my-4" />

                        <div className='verify-step-instructions'>We will send you an email to the email address below. If the email address below is incorrect, please update it below now, then click "SEND EMAIL NOW".</div>
                    </div>

                    {/*We misuse isDirty here to show success state when this field is updated (not dirty)*/}
                    <TextInput name="emailAddress" type="email" label="Email Address" hideLabel={true} 
                        overlay={buttonOverlay} isDirty={!emailIsDirty}
                        options={VALIDATION_SCHEMA.emailAddress} errors={errors} register={register} />

                    <div className="align-with-inputs mb-5">
                        <Button variant="primary" className="ppm-button-primary" type="button" onClick={handleEmailVerification}>
                            SEND EMAIL NOW
                        </Button>
                    </div>
                </>
            }

            {emailVerified &&
                <div className="align-with-inputs mb-5">
                    <div className='verify-step-label'>Your email address below has been successfully verified.</div>
                    <hr className="my-4" />
                    <div className="ml-3">{emailAddress}</div>
                </div>    
            }

            {!emailVerified && (confirmationRquested !== 0) &&
                <Form>
                    <div className="align-with-inputs">
                        <div className='verify-step-label'>{isCommunity ? <>Step 1:&nbsp;</> : <></>}Please verify your email address below.</div>

                        <hr className="my-4" />

                        <div className="d-flex justify-content-center mb-4">
                            <div className='w-75 text-center'>
                                <div>We sent your verification code to {emailAddress}.</div>
                                <div>This code will expire in 10 minutes.</div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <TextInput name="emailCode" label="Enter verification code" placeholder="Verification Code"
                            options={VALIDATION_SCHEMA.emailCode} errors={errors} register={register} />

                        <div className="align-with-inputs mb-5">
                            <Button variant="primary" className="ppm-button-primary mr-1" type="button" onClick={handleEmailCheck} style={{ height: "45px" }}>
                                VERIFY EMAIL
                            </Button>
                            <Button variant={"secondary"}
                                className={"ppm-button-secondary"}
                                type="button"
                                style={{ height: "45px" }}
                                onClick={handleEmailVerification} >
                                RESEND CODE
                            </Button>
                            {(confirmationRquested > 1) &&
                                <p class="error-message">If you still have not received your code, please check your spam folder for an email from <a style={{ color: "#1C77B0" }} href="mailto:pittplusme@pitt.edu">pittplusme@pitt.edu</a>.</p>
                            }
                        </div>
                    </div>
                </Form>
            }
        </>   
    );
}

// front-end timeouts intentionlly longer than server by 5 seconds       
export const retryTimeouts = [0, 35, 45, 65, 95, 125];
export const maxRetrySeconds = 605;

export function PhoneVerify({
    errors, register, getValues, resetField, setError, fieldUpdate, setValue,
    phoneNumber, phoneIsDirty, emailVerified, channel, isCommunity,
    initialAttempts, trigger, handleServerErrors, clearErrorMessage
}) {

    const bearer = useBearerTokenContext();
    const tenant = useTenantContext();

    const channelTypes = {
        SMS: 'sms',
        Call: 'call'
    }

    const [phoneRequestAttempts, setPhoneRequestAttempts] = useState(initialAttempts);
    const [phoneIsSMSCapable, setPhoneIsSMSCapable] = useState(false);

    // resend code timer https://www.twilio.com/en-us/blog/best-practices-retry-logic-sms-2fa#best-practices-for-retrying-sms-2fa-codes
    const timeNow = new Date();
    const resendCodeTimer = useTimer({ expiryTimestamp: timeNow, autoStart: false });
    const timerFiredOnload = useRef(false);

    // useEffect so that page load with outstanding phoneRequestAttempts
    // restarts timer
    useEffect(() => {
        if (initialAttempts > 0 && timerFiredOnload.current === false) {
            const timeCounter = new Date();
            const currentWaitTime = (initialAttempts < retryTimeouts.length) ? retryTimeouts[initialAttempts] : maxRetrySeconds;

            timerFiredOnload.current = true;
            timeCounter.setSeconds(timeCounter.getSeconds() + currentWaitTime);
            resendCodeTimer.restart(timeCounter);
        }
    }, [initialAttempts, resendCodeTimer, timerFiredOnload]);

    useEffect(() => {

        if (!phoneNumber || phoneIsDirty)
            return;

        const phoneLookupData = { 'phoneNumber': phoneNumber };

        lookupPhoneLine(phoneLookupData, bearer, tenant).then(
            (value) => {
                if (value && value.valid && value.isSMSCapable) {
                    setPhoneIsSMSCapable(true);
                    setValue('channel', channelTypes.SMS);
                } else {
                    setPhoneIsSMSCapable(false);                    
                    setValue('channel', channelTypes.Call);
                }
            },
            (reason) => {
                console.log(reason);
                setPhoneIsSMSCapable(false);                
                setValue('channel', channelTypes.Call);
            });

    }, [setPhoneIsSMSCapable, setValue, phoneIsDirty, phoneNumber, bearer, tenant, channelTypes.Call, channelTypes.SMS])

    const disableCall = (phoneIsSMSCapable && phoneRequestAttempts < 3 && isCommunity)

    let stepInstructions = 'We will send you a verification code to the phone number below. If the phone number below is incorrect, please update it below now. '
    let resendInstructions = (<></>);

    if (phoneIsSMSCapable && isCommunity) {
        stepInstructions += 'Then click "SEND CODE NOW". The voice option will be available after three unsuccessful SMS attempts.';
        resendInstructions = (<div>The voice option will be available after three unsuccessful SMS attemptes.</div>);
    } else {
        stepInstructions += 'Then select whether you want your code sent via SMS or by voice and click "SEND CODE NOW".';
    }


    // verify functions
    const handlePhoneRequest = () => {

        if (resendCodeTimer.isRunning)
            return;

        if (phoneIsDirty) {
            setError('phoneNumber', { type: 'server', message: 'Unsaved phone changes' });
            return;
        }

        // make sure fields are valid before continuing 
        trigger(['phoneNumber', 'channel']).then(triggerResult => {
            if (triggerResult === false) return;

            const [phoneNumberGuid] = getValues(['phoneNumberGuid']);
            const phoneNumberRequestVerify = {
                'phoneNumber': phoneNumber,
                'phoneNumberGuid': phoneNumberGuid,
                'channel': channel,
                'isSMSCapable': phoneIsSMSCapable
            };

            requestPhoneVerification(phoneNumberRequestVerify, bearer, tenant).then(
                (value) => {
                    clearErrorMessage();

                    if (value === true) {
                        const newPhoneRequestAttempts = phoneRequestAttempts + 1;
                        setPhoneRequestAttempts(newPhoneRequestAttempts);

                        // reset resend code timer
                        const timeCounter = new Date();
                        const currentWaitTime = (newPhoneRequestAttempts < retryTimeouts.length) ? retryTimeouts[newPhoneRequestAttempts] : maxRetrySeconds;
                        timeCounter.setSeconds(timeCounter.getSeconds() + currentWaitTime);
                        resendCodeTimer.restart(timeCounter);
                    }
                },
                (reason) => {
                    handleServerErrors(reason, false);
                });
        });
    }

    const handlePhoneCheck = () => {

        if (phoneIsDirty) {
            setError('phoneNumber', { type: 'server', message: 'Unsaved phone changes' });
            return;
        }

        trigger(['phoneNumber', 'phoneCode']).then(triggerResult => {
            if (triggerResult === false) return;

            const [phoneNumberGuid, phoneCode] = getValues(['phoneNumberGuid', 'phoneCode']);
            const phoneNumberCheckVerify = {
                'phoneNumber': phoneNumber,
                'phoneNumberGuid': phoneNumberGuid,
                'channel': channel,
                'phoneCode': phoneCode
            };

            checkPhoneVerification(phoneNumberCheckVerify, bearer, tenant).then(
                (value) => {
                    clearErrorMessage();
                    if (value === true) {
                        setValue('phoneVerified', true);
                    } else {
                        setError('phoneCode', { type: 'server', message: 'Error verifying phone code' });
                    }
                },
                (reason) => {
                    handleServerErrors(reason, false);
                });
        });
    }

    // update buttons to put over the phone field
    const buttonOverlay = phoneIsDirty ? (
        <>
            <Button variant="" className="error" title="Cancel phone number change"
                onClick={() => resetField('phoneNumber')}>X</Button>
            <Button variant="" className="success" title="Confirm phone number change"
                onClick={() => fieldUpdate(phoneNumber, 'newNumber', 'phoneNumber', 'phone number')}>&#10003;</Button>
        </>
    ) : <></>;

    const opacity = (emailVerified) ? "" : "opacity75";

    return (
        <div className={opacity}>
            <div className="align-with-inputs">
                <div className='verify-step-label'>Step 2: Please verify your phone number below.</div>

                <hr className="my-4" />

                {phoneRequestAttempts === 0 &&
                     <div className='verify-step-instructions'>{stepInstructions}</div>
                }

                {!emailVerified &&
                    <div className="ml-3">{phoneNumber}</div>
                }
            </div>
            {emailVerified &&
                <Form>

                    {phoneRequestAttempts === 0 &&
                        <TextInput name="phoneNumber" type="text" label="Phone Number" hideLabel={true} 
                            overlay={buttonOverlay} isDirty={!phoneIsDirty}
                            options={VALIDATION_SCHEMA.phoneNumber} errors={errors} register={register} />
                    }

                    {phoneRequestAttempts !== 0 &&
                        <>
                            {/*https://www.twilio.com/docs/verify/developer-best-practices#display-complete-contact-information-for-initial-user-verification*/}
                            <div className="d-flex justify-content-center mb-4">
                                <div className='w-75 text-center'>
                                    <div>We sent your verification code to {phoneNumber}.</div>
                                    <div>This code will expire in 10 minutes.</div>
                                </div>
                            </div>

                            <TextInput name="phoneCode" label="Enter verification code" placeholder="Verification Code"
                            options={VALIDATION_SCHEMA.phoneCode} errors={errors} register={register} />

                            <div className="align-with-inputs">
                                <Button variant="primary" className="ppm-button-primary mb-5" type="button" onClick={handlePhoneCheck} style={{ height: "45px" }}>
                                    VERIFY NUMBER
                                </Button>
                                <div>Choose how you would like us to resend your code and click RESEND CODE.</div>
                                {resendInstructions}
                            </div>
                        </>
                    }
                    {/*Offer alternate channels like Voice on the 3rd verification attempt:
                        https://www.twilio.com/en-us/blog/best-practices-retry-logic-sms-2fa#best-practices-for-fallback-channels
                        */}
                    <CheckboxInput name="channel" label="How would you like to receive your code?" hideLabel={true}
                        values={[
                            { name: 'Send Via SMS Message', guid: channelTypes.SMS },
                            { name: 'Send By Voice', guid: channelTypes.Call, disabled: disableCall}]}
                        type="radio" options={VALIDATION_SCHEMA.channel} errors={errors} register={register} />

                    <div className="align-with-inputs">
                        <div>
                            <Button variant={(phoneRequestAttempts === 0) ? "primary" : "secondary"}
                                className={(phoneRequestAttempts === 0) ? "ppm-button-primary" : "ppm-button-secondary"}
                                type="button"
                                style={{height:"45px"} }
                                onClick={handlePhoneRequest}
                                disabled={resendCodeTimer.isRunning} >
                                {(phoneRequestAttempts === 0) ? "SEND CODE NOW" : "RESEND CODE"}
                            </Button>
                            <div className="resendcode-countdown mt-3">
                                {resendCodeTimer.isRunning &&
                                    <>
                                        {`RESEND CODE in 
                                            ${resendCodeTimer.minutes > 0 ? `${resendCodeTimer.minutes + 1} minute(s)` : ''}
                                            ${resendCodeTimer.minutes === 0 ? `${resendCodeTimer.seconds} seconds` : ''}
                                        `}
                                    </>
                                }
                            </div>                                        
                        </div>
                    </div>

                </Form>
            }

        </div>
    );
}